import { useEffect, useRef, useState } from "react";

import { getAreaLandmarksInBounds } from "@/api/areaCluster";

/**
 * 확대했을 때 지도에 얹는 **작은 이름표**.
 *
 * 상권 칩과 하는 일이 다르다.
 *   상권 칩 : 눌러서 그 동네로 들어가는 것. 넓게 볼 때. 숫자가 붙는다.
 *   이름표  : 읽는 것. 핀이 보이는 단계에서 "여기가 홍대 놀이터 앞" 을 알려 준다.
 *             숫자 없음, 눌러도 아무 일 없음.
 *
 * 그래서 **칩이 사라지는 배율부터** 뜬다. 둘이 같이 뜨면 화면이 시끄럽다.
 */
const CACHE_TTL_MS = 3 * 60 * 1000;
const CACHE_MAX = 40;

// ⚠️ 좌표를 **격자에 맞춰** 쓴다(소수 셋째 자리 ≈ 110m).
// 예전에는 열쇠만 반올림하고 **요청은 원본 좌표로** 보냈다. 지도가 아주 조금 흔들리면
// 열쇠가 매번 달라져 캐시가 안 맞고 계속 다시 불렀다 — 실측으로 한 손님이 8시간 동안
// 89,574번(초당 최대 9번) 불렀다. 요청도 같은 격자로 보내면 그 흔들림이 한 요청으로 모인다.
const QUANT_DECIMALS = 3;
const quantize = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return n;
  const f = 10 ** QUANT_DECIMALS;
  return Math.round(n * f) / f;
};

const quantizeBounds = (bounds) => ({
  south: quantize(bounds.south),
  west: quantize(bounds.west),
  north: quantize(bounds.north),
  east: quantize(bounds.east),
});

const boundsKey = (bounds) =>
  [bounds.south, bounds.west, bounds.north, bounds.east]
    .map((v) => Number(v).toFixed(QUANT_DECIMALS))
    .join(",");

export const useAreaLandmarks = ({ bounds, zoom, minZoom, enabled = true }) => {
  const [landmarks, setLandmarks] = useState([]);
  const cacheRef = useRef(new Map());
  const requestIdRef = useRef(0);
  const inFlightKeyRef = useRef(null);

  useEffect(() => {
    if (!enabled || !bounds || !Number.isFinite(zoom)) return;

    if (zoom < minZoom) {
      setLandmarks([]);
      return;
    }

    const snapped = quantizeBounds(bounds);
    const key = boundsKey(snapped);

    // 같은 자리를 지금 부르는 중이면 또 부르지 않는다(고리 차단용 두 번째 자물쇠).
    if (inFlightKeyRef.current === key) return;

    const cached = cacheRef.current.get(key);
    if (cached && Date.now() - cached.at < CACHE_TTL_MS) {
      setLandmarks(cached.items);
      return;
    }

    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;

    // ⚠️ 지도를 한 번 끄는 동안 이 훅이 예닐곱 번 불린다(카메라가 몇 번에 나눠 도착한다).
    //    예전에는 그때마다 조회가 나가서, 끌 때마다 같은 성격의 요청이 6~9건씩 나갔다
    //    (2026-08-10 실측). 멈춘 화면에서 **한 번만** 나가게 기다렸다 부른다.
    const timer = setTimeout(() => {

      inFlightKeyRef.current = key;
      getAreaLandmarksInBounds({
        south: snapped.south,
        west: snapped.west,
        north: snapped.north,
        east: snapped.east,
        limit: 12,
      })
        .then((items) => {
          if (requestId !== requestIdRef.current) return;
          const list = Array.isArray(items) ? items : [];
          cacheRef.current.set(key, { at: Date.now(), items: list });
          if (cacheRef.current.size > CACHE_MAX) {
            const oldest = cacheRef.current.keys().next().value;
            if (oldest) cacheRef.current.delete(oldest);
          }
          setLandmarks(list);
        })
        .catch(() => {
          // 이름표를 못 받아도 지도는 그대로 쓴다. 핀은 따로 온다.
        })
        .finally(() => {
          if (inFlightKeyRef.current === key) inFlightKeyRef.current = null;
        });
    }, 250);

    return () => clearTimeout(timer);
  }, [bounds, zoom, minZoom, enabled]);

  return landmarks;
};
